import { SustainabilityMatterId } from '@upright/common/model/input/materiality-assessment/types-matters';
import { CsrdViewSubPage } from '../../types';
import { CsrdSingleMatterViewMaterialityTypePath } from './types';
import { MaterialityType } from '@upright/common/model/input/materiality-assessment/types';

import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { InternalCompanyViewMode } from '@upright/ui/untidy/urls';
import { ViewMode } from '@upright/ui/views/model-entity-views/shared/view-modes';

// Note: When adding new standalone lens paths (like FINANCIAL_EFFECTS), make sure to handle them
// in useCsdrMaterialityResultsBasePath so that navigation from those lenses to DMA single matter
// views produces the correct path.

export function useCsdrSingleMatterViewPathGetter() {
  const materialityResultsBasePath = useCsdrMaterialityResultsBasePath();

  return useCallback(
    (matterId: SustainabilityMatterId, materialityType: MaterialityType) => {
      return getCompanyCsrdSingleMatterViewUrl(materialityResultsBasePath, matterId, materialityType);
    },
    [materialityResultsBasePath],
  );
}

/**
 * Custom hook that builds the base path for CSRD materiality results.
 * Handles both on-demand and regular CSRD views, as well as navigating from
 * the standalone Financial Effects lens to the DMA single matter view paths.
 * @returns The base path string for materiality results
 */
export function useCsdrMaterialityResultsBasePath(): string {
  const { pathname } = useLocation();

  const isOnDemand = pathname.includes(InternalCompanyViewMode.CSRD_ON_DEMAND_VIEW);
  const isOnFinancialEffects = pathname.includes(ViewMode.FINANCIAL_EFFECTS);
  const viewMode = isOnDemand ? InternalCompanyViewMode.CSRD_ON_DEMAND_VIEW : ViewMode.CSRD;

  // When on the Financial Effects standalone path, split on that segment to get the company base
  // path, then navigate to the DMA (CSRD) path. Without this, the path would incorrectly become
  // e.g. /financial-effectsdma/materiality-results (no slash between them).
  const splitOn = isOnFinancialEffects ? ViewMode.FINANCIAL_EFFECTS : viewMode;
  const [basePathSection] = pathname.split(splitOn);

  return `${basePathSection}${viewMode}/${CsrdViewSubPage.MATERIALITY_RESULTS}`;
}

function getCompanyCsrdSingleMatterViewUrl(
  materialityResultsBasePath: string,
  matterIdPath: SustainabilityMatterId,
  materialityType: MaterialityType,
) {
  const materialityTypePath =
    materialityType === MaterialityType.IMPACT
      ? CsrdSingleMatterViewMaterialityTypePath.IMPACT
      : CsrdSingleMatterViewMaterialityTypePath.FINANCIAL;

  return `${materialityResultsBasePath}/${matterIdPath}/${materialityTypePath}`;
}
